/**
 * Secura Web : liste des projets style messagerie (avatar membres, nom, description).
 * En-tête profil (avatar, nom, poste, paramètres) + onglets Actifs / En cours / Suspendu.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Loader2, PauseCircle, Plus } from 'lucide-react';
import axiosInstance from '../../config/axios';
import { AppContext } from '../../contexts/AppContext';
import { useSocketOptional } from '../../contexts/SocketContext';
import { WEB_SIDEBAR_WIDTH, WEB_NAVBAR_HEIGHT } from '../../utils/webHost';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { assets } from '../../assets/assets';
import { getPhotoUrl } from '../../utils/imageUtils';
import { cn } from '@/lib/utils';
import WebProfileHeader from './WebProfileHeader';

const FILTERS = [
  { key: 'actifs', label: 'Actifs', icon: MessageCircle, test: (p) => p.statut === 'planifie' },
  { key: 'en_cours', label: 'En cours', icon: Loader2, test: (p) => p.statut === 'en_cours' },
  { key: 'suspendu', label: 'Suspendu', icon: PauseCircle, test: (p) => p.statut === 'en_pause' },
];

const fold = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function memberPhoto(m) {
  const raw = m?.profileImageUrl;
  if (!raw) return null;
  return getPhotoUrl(raw);
}

function ProjectAvatars({ members = [] }) {
  const shown = (members.length ? members : [{ id: 'fallback', nom_complet: '?', profileImageUrl: null }]).slice(0, 3);
  const extra = members.length > 3 ? members.length - 3 : 0;

  return (
    <div className="flex -space-x-3 shrink-0">
      {shown.map((m) => {
        const src = m.id === 'fallback' ? null : memberPhoto(m);
        return (
          <Avatar key={m.id} className="h-10 w-10">
            {src ? (
              <AvatarImage src={src} alt="" className="object-cover" />
            ) : (
              <AvatarImage src={assets.employes} alt="" className="object-cover" />
            )}
            <AvatarFallback className="bg-transparent p-0">
              <img src={assets.employes} alt="" className="h-full w-full object-cover" />
            </AvatarFallback>
          </Avatar>
        );
      })}
      {extra > 0 ? (
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-gray-100 text-gray-600 text-xs font-bold">+{extra}</AvatarFallback>
        </Avatar>
      ) : null}
    </div>
  );
}

export default function WebProjectsSidebar({ menuOffset = 0, embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket } = useSocketOptional();
  const { userData, interimStatus } = useContext(AppContext);
  const canCreate = !!(userData?.isResponsableDepartement || interimStatus?.isInterim);
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('actifs');
  const [cursor, setCursor] = useState(-1);
  const refreshTimer = useRef(null);
  const listRef = useRef(null);

  const activeId = Number((/^\/projets\/(\d+)/.exec(location.pathname) || [])[1]) || null;
  const readingId =
    activeId && !location.pathname.includes('/tasks') && new URLSearchParams(location.search).get('tab') === 'conversation'
      ? activeId
      : null;

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/projets/sidebar');
      setProjets(Array.isArray(data?.data) ? data.data : []);
    } catch {
      // liste inchangée
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onSearch = (e) => setQuery(String(e.detail ?? ''));
    window.addEventListener('secura-web-project-search', onSearch);
    return () => window.removeEventListener('secura-web-project-search', onSearch);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const refresh = () => {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(load, 1500);
    };
    socket.on('badge:update', refresh);
    return () => {
      socket.off('badge:update', refresh);
      clearTimeout(refreshTimer.current);
    };
  }, [socket, load]);

  useEffect(() => {
    if (!readingId) return;
    setProjets((list) =>
      list.some((p) => p.id === readingId && p.unreadCount)
        ? list.map((p) => (p.id === readingId ? { ...p, unreadCount: 0 } : p))
        : list
    );
  }, [readingId, projets]);

  const shown = useMemo(() => {
    const q = fold(query.trim());
    if (q) {
      return projets.filter(
        (p) => fold(p.nom).includes(q) || fold(p.description).includes(q)
      );
    }
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    return projets.filter((p) => f.test(p));
  }, [projets, query, filter]);

  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, projets.filter((p) => f.test(p)).length])),
    [projets]
  );

  const open = useCallback((p) => navigate(`/projets/${p.id}/tasks?filter=todo`), [navigate]);

  useEffect(() => setCursor(query ? 0 : -1), [query]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && shown.length) {
        e.preventDefault();
        const i = shown.findIndex((p) => p.id === activeId);
        const next = e.key === 'ArrowDown' ? Math.min(shown.length - 1, i + 1) : Math.max(0, i === -1 ? 0 : i - 1);
        open(shown[next]);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shown, activeId, open]);

  useEffect(() => {
    if (cursor < 0) return;
    listRef.current?.querySelector(`[data-row="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  return (
    <aside
      className={
        embedded
          ? 'hidden lg:flex flex-col bg-[#f7f8fa] border-r border-gray-200 shrink-0 h-full'
          : 'hidden lg:flex fixed bottom-0 flex-col bg-[#f7f8fa] border-r border-gray-200 z-30'
      }
      style={
        embedded
          ? { width: WEB_SIDEBAR_WIDTH }
          : { top: WEB_NAVBAR_HEIGHT, left: menuOffset, width: WEB_SIDEBAR_WIDTH }
      }
      aria-label="Mes projets"
    >
      <WebProfileHeader />

      {/* Onglets Actifs / En cours / Suspendu */}
      <div className="px-3 pt-2 bg-white border-b border-gray-100 shrink-0">
        <div className="flex items-stretch justify-between gap-1" role="tablist" aria-label="Filtrer les projets">
          {FILTERS.map((f) => {
            const on = f.key === filter && !query;
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  setQuery('');
                  setFilter(f.key);
                }}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 pb-2.5 pt-1.5 text-xs font-bold transition-colors border-b-[3px] rounded-t-lg cursor-pointer',
                  on
                    ? 'text-[#0026E2] border-[#0026E2] bg-white'
                    : 'text-gray-400 border-transparent hover:text-gray-600'
                )}
              >
                <Icon size={18} className={on && f.key === 'en_cours' ? '' : undefined} />
                <span>
                  {f.label}
                  {counts[f.key] ? <span className="ml-1 font-semibold opacity-70">({counts[f.key]})</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste projets */}
      <div ref={listRef} className="flex-1 overflow-y-auto relative">
        {loading ? (
          <div className="p-4 space-y-4" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-2/3 rounded bg-gray-200 animate-pulse" />
                  <div className="h-3 w-5/6 rounded bg-gray-200 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : !shown.length ? (
          <p className="text-sm text-gray-500 text-center px-6 py-10">
            {query
              ? 'Aucun projet trouvé.'
              : filter === 'en_cours'
                ? 'Aucun projet en cours.'
                : filter === 'suspendu'
                  ? 'Aucun projet suspendu.'
                  : 'Aucun projet actif.'}
          </p>
        ) : (
          <ul className="py-2">
            {shown.map((p, i) => {
              const active = p.id === activeId;
              const count = p.unreadCount || 0;
              const highlighted = i === cursor;
              const subtitle = (p.description && String(p.description).trim()) || previewFallback(p, userData?.id);
              return (
                <li key={p.id}>
                  <button
                    data-row={i}
                    type="button"
                    onClick={() => open(p)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer',
                      active ? 'bg-white shadow-sm' : highlighted ? 'bg-white/70' : 'hover:bg-white/80'
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <ProjectAvatars members={p.members || []} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block text-sm truncate', count ? 'font-bold text-[#0B3A4A]' : 'font-bold text-[#0B3A4A]')}>
                        {p.nom}
                      </span>
                      <span className={cn('block text-xs truncate mt-0.5', count ? 'text-gray-700' : 'text-gray-500')}>
                        {subtitle}
                      </span>
                    </span>
                    {count ? (
                      <span className="shrink-0 min-w-[22px] h-5 px-1.5 rounded-full bg-[#0026E2] text-white text-[11px] font-bold flex items-center justify-center">
                        {count > 99 ? '99+' : count}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {canCreate ? (
          <button
            type="button"
            onClick={() => navigate('/projets/create')}
            className="absolute bottom-5 right-5 w-12 h-12 rounded-full bg-[#0026E2] hover:bg-[#0019B3] text-white shadow-lg flex items-center justify-center"
            title="Nouveau projet"
            aria-label="Nouveau projet"
          >
            <Plus size={22} />
          </button>
        ) : null}
      </div>
    </aside>
  );
}

function previewFallback(p, userId) {
  const m = p.lastMessage;
  if (!m) return 'Aucune description';
  if (m.systeme) return m.texte;
  const who = Number(m.auteur_id) === Number(userId) ? 'Vous' : String(m.auteur_nom || '').trim().split(/\s+/)[0];
  return who ? `${who} : ${m.texte}` : m.texte;
}
